import { useEffect, useState } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Content,
  Page,
  Header,
  InfoCard,
} from '@backstage/core-components';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  extensionCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardBody: {
    flex: 1,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  installRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  toolList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    '& li': {
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
    },
  },
}));

interface ExtensionEntity {
  metadata: {
    name: string;
    title?: string;
    description?: string;
    annotations?: Record<string, string>;
    tags?: string[];
  };
  spec?: {
    owner?: string;
    tools?: Array<{ name: string; description: string }>;
  };
}

const STATIC_INTEGRATIONS: Array<{
  name: string;
  title: string;
  description: string;
  tags: string[];
  tools: Array<{ name: string; description: string }>;
  annotation: string;
}> = [
  {
    name: 'sonarqube',
    title: 'SonarQube',
    description:
      'Automatically activates the SonarQube MCP server for this component in Copilot chat. Copilot will use the project key to fetch issues, quality gate status, code coverage, security hotspots, and code smells directly from SonarQube.',
    tags: ['code-quality', 'security', 'sast'],
    tools: [
      { name: 'issues', description: 'List and filter code quality and security issues' },
      { name: 'quality_gate', description: 'Inspect quality gate status and conditions' },
      { name: 'measures', description: 'Retrieve code metrics, coverage, and duplication data' },
      { name: 'security_hotspots', description: 'List security hotspots requiring manual review' },
    ],
    annotation: 'sonarqube.org/project-key: <your-project-key>',
  },
];

export function CopilotExtensionMarketplace() {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [extensions, setExtensions] = useState<ExtensionEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    fetch(`${backendUrl}/api/catalog/entities?filter=kind=Component,spec.type=copilot-extension&limit=50`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: ExtensionEntity[]) => setExtensions(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [backendUrl]);

  return (
    <Page themeId="tool">
      <Header
        title="Copilot Extensions Marketplace"
        subtitle="Browse and install reusable tool packs for Copilot chat"
      />
      <Content>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">Failed to load extensions: {error}</Alert>}
        {!loading && !error && extensions.length === 0 && (
          <Alert severity="info">
            No CopilotExtension entities found. Publish a YAML with <code>kind: CopilotExtension</code> to the catalog to get started.
          </Alert>
        )}
        <Grid container spacing={3}>
          {STATIC_INTEGRATIONS.map(integration => (
            <Grid key={integration.name} item xs={12} sm={6} md={4}>
              <InfoCard title={integration.title} className={classes.extensionCard}>
                <div className={classes.cardBody}>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {integration.description}
                  </Typography>

                  {integration.tags.length > 0 && (
                    <div className={classes.tags}>
                      {integration.tags.map(tag => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </div>
                  )}

                  {integration.tools.length > 0 && (
                    <>
                      <Typography variant="caption" style={{ marginTop: 12, display: 'block' }}>
                        <strong>Tools ({integration.tools.length})</strong>
                      </Typography>
                      <ul className={classes.toolList}>
                        {integration.tools.map(t => (
                          <li key={t.name}>
                            <strong>{t.name}</strong> — {t.description}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                <div className={classes.installRow}>
                  <Typography variant="caption" color="textSecondary">
                    Add <code>{integration.annotation}</code> to your component annotation to enable.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => navigator.clipboard.writeText(integration.annotation)}
                  >
                    Copy Annotation
                  </Button>
                </div>
              </InfoCard>
            </Grid>
          ))}
          {extensions.map(ext => {
            const tools = ext.spec?.tools ?? [];
            const tags = ext.metadata.tags ?? [];
            const name = ext.metadata.name;
            const title = ext.metadata.title ?? name;
            const description = ext.metadata.description ?? 'No description provided.';
            const owner = ext.spec?.owner ?? 'unknown';

            return (
              <Grid key={name} item xs={12} sm={6} md={4}>
                <InfoCard title={title} className={classes.extensionCard}>
                  <div className={classes.cardBody}>
                    <Typography variant="body2" color="textSecondary">
                      Owner: {owner}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      {description}
                    </Typography>

                    {tags.length > 0 && (
                      <div className={classes.tags}>
                        {tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </div>
                    )}

                    {tools.length > 0 && (
                      <>
                        <Typography variant="caption" style={{ marginTop: 12, display: 'block' }}>
                          <strong>Tools ({tools.length})</strong>
                        </Typography>
                        <ul className={classes.toolList}>
                          {tools.map(t => (
                            <li key={t.name}>
                              <strong>{t.name}</strong> — {t.description}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  <div className={classes.installRow}>
                    <Typography variant="caption" color="textSecondary">
                      Add <code>copilot.kyndemo.live/extensions: {name}</code> to your component annotation to enable.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `copilot.kyndemo.live/extensions: ${name}`,
                        )
                      }
                    >
                      Copy Annotation
                    </Button>
                  </div>
                </InfoCard>
              </Grid>
            );
          })}
        </Grid>
      </Content>
    </Page>
  );
}
